import { useState, useEffect, useRef, useCallback } from "react";
import "../asset/css/Hero.css";

function useCountUp(ref, target) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let rafId;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const duration = 1200;
      const start = performance.now();
      const tick = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.round(eased * target).toLocaleString();
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(rafId); };
  }, [ref, target]);
}

function CountUp({ target, suffix = "" }) {
  const ref = useRef(null);
  useCountUp(ref, target);
  return <><span ref={ref}>0</span>{suffix}</>;
}

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("visible");
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const STATS = [
  { target: 48000, suffix: "+", label: "Artists Competed" },
  { target: 120,   suffix: "+", label: "Countries" },
  { target: 50,    suffix: "K", label: "Prize Pool $" },
  { target: 200,   suffix: "+", label: "Expert Judges" },
  { target: 4,     suffix: "",  label: "Seasons Run" },
];

const GENRES = ["🎸 Rock","🎹 EDM","🎤 Vocal","🎵 Hip-Hop","🎺 Jazz","🎻 Classical","🥁 World","🎙️ Pop"];

const STRIP_IMGS = [
  "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&q=70",
  "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&q=70",
  "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=400&q=70",
  "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&q=70",
  "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&q=70",
  "https://images.unsplash.com/photo-1571327073757-71d13b1e23dc?w=400&q=70",
];

const WAVE_HEIGHTS = [12,22,32,18,28,38,20,14,30,24,36,16,26,34,10,28,20,38,14,24,32,18,28,12,36,22,30,16,26,38,12,22,32,18,28,38];

const Hero = () => {
  const [scrolled, setScrolled] = useState(false);

  useReveal();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 60);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="h2-hero" id="hero">

      <div className="h2-hero-img" aria-hidden="true" />
      <div className="h2-hero-overlay" aria-hidden="true" />

      <div className="h2-orb h2-orb1" aria-hidden="true" />
      <div className="h2-orb h2-orb2" aria-hidden="true" />
      <div className="h2-orb h2-orb3" aria-hidden="true" />

      {/* top meta bar */}
      <div className="h2-meta">
        <div className="h2-meta-inner">
          <span className="h2-live-pill">
            <span className="h2-live-dot" />
            LIVE · SEASON 4
          </span>
          <span className="h2-meta-divider" />
          <span className="h2-meta-text">Submissions close Apr 30, 2025</span>
          <span className="h2-meta-divider" />
          <span className="h2-meta-text">120+ Countries · 8 Genres</span>
        </div>
      </div>

      {/* central content */}
      <div className="h2-center">
        <div className="h2-title-wrap">
          <span className="h2-title-sub">THE WORLD'S BIGGEST</span>
          <h1 className="h2-title">
            <span className="h2-t1">SOUND</span>
            <span className="h2-t2">BATTLE</span>
          </h1>
          <p className="h2-tagline">
            Upload. Compete. Conquer. — <em>$50,000 Prize Pool awaits.</em>
          </p>
        </div>

        <div className="h2-wave" aria-hidden="true">
          {WAVE_HEIGHTS.map((h, i) => (
            <span key={i} style={{ "--bar-h": `${h}px`, animationDelay: `${((i * 0.13) % 1).toFixed(2)}s` }} />
          ))}
        </div>

        <div className="h2-actions">
          <a href="#signup" className="h2-btn-enter">
            <span className="h2-btn-icon">⚡</span>
            Enter the Battle
          </a>
          <a href="#how-it-works" className="h2-btn-watch">
            <span className="h2-play">▶</span>
            How It Works
          </a>
        </div>

        <div className="h2-stats">
          {STATS.map((s, i) => (
            <div className="h2-stat" key={i}>
              <span className="h2-stat-num">
                <CountUp target={s.target} suffix={s.suffix} />
              </span>
              <span className="h2-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* floating genre pills */}
      <div className="h2-pills" aria-hidden="true">
        {GENRES.map((g, i) => (
          <span className="h2-pill" key={g} style={{ animationDelay: `${(i * 0.4).toFixed(1)}s` }}>{g}</span>
        ))}
      </div>

      {/* bottom image strip */}
      <div className="h2-img-strip">
        {STRIP_IMGS.map((src, i) => (
          <div className="h2-strip-item" key={i}>
            <img src={src} alt="" loading="lazy" decoding="async" width="400" height="160" />
            <div className="h2-strip-overlay" />
          </div>
        ))}
      </div>

      <div className="h2-scroll-cue" aria-hidden="true">
        <span>SCROLL</span>
        <div className="h2-scroll-line" />
      </div>

    </section>
  );
};

export default Hero;